import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowUpRight } from 'lucide-react';
import { MaskedLines } from './Reveal';

const HERO_VIDEO = 'https://customer-assets-cm19k8pv.emergentagent.net/job_occult-secrets/artifacts/0gr9wwop_video-1.mp4';

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);

  return (
    <section ref={ref} id="top" className="relative pt-36 lg:pt-44 pb-20 lg:pb-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-mono text-xs tracking-[0.3em] uppercase text-gold mb-8"
            data-testid="hero-overline"
          >
            The León Foundation — Est. 1842
          </motion.p>
          <h1 data-testid="hero-heading" className="font-serif font-medium text-[#F2E5CF] text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            <MaskedLines
              lines={[
                'A legacy of',
                'breakthroughs,',
                <span key="l3">four <em className="text-gold not-italic border-b-[3px] border-gold/60">generations</em> deep.</span>,
              ]}
              delay={0.3}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-[#B99C8B] leading-relaxed"
          >
            From the cliffs above St. Bellmore, the León family advances law, medicine
            and science — as it has, without interruption, since 1842.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#pillars"
              data-testid="hero-cta-work"
              className="group inline-flex items-center gap-2 bg-[#C9A227] text-[#190A0B] font-display font-semibold text-sm tracking-wide px-7 py-3.5 hover:bg-[#F2E5CF] transition-colors duration-300"
            >
              Explore our work
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href="#family"
              data-testid="hero-cta-family"
              className="group inline-flex items-center gap-2 border border-[#542826] text-[#F2E5CF] font-display font-semibold text-sm tracking-wide px-7 py-3.5 hover:border-[#C9A227] transition-colors duration-300"
            >
              Meet the family
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-14 flex items-center gap-10 font-mono text-xs tracking-[0.2em] text-[#94756A]"
          >
            <span><span className="text-[#F2E5CF] text-lg font-semibold tracking-normal font-display">184</span> YRS</span>
            <span className="w-px h-8 bg-[#47201F]" />
            <span><span className="text-[#F2E5CF] text-lg font-semibold tracking-normal font-display">04</span> DISCIPLINES</span>
            <span className="w-px h-8 bg-[#47201F]" />
            <span><span className="text-[#F2E5CF] text-lg font-semibold tracking-normal font-display">01</span> TOWN</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-gold/50 translate-x-4 translate-y-4" aria-hidden="true" />
            <div className="relative overflow-hidden aspect-[4/5] bg-[#271011]" data-testid="hero-image-frame">
              <motion.video
                src={HERO_VIDEO}
                autoPlay
                muted
                loop
                playsInline
                style={{ y: imgY }}
                className="w-full h-[115%] object-cover sepia-[0.3] brightness-[0.82] contrast-[1.08] saturate-[0.8]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] text-white/70 uppercase">
                Foundation Hall · St. Bellmore Bluff
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
